/**
 * Which catalog rows the plan cards and the full comparison table show.
 *
 * React-free on purpose (AGENTS.md §4): "what does this plan add over the previous one"
 * and "how do fourteen widget keys collapse into three lines" are pure data transforms.
 * Keeping them out of `PlanComparison.tsx` leaves that file with rendering only, and lets
 * these decisions be read (and tested) without mounting anything.
 */
import { UNLIMITED, type EntitlementCatalogEntry, type EntitlementMap } from "./types.js";

/**
 * Category blocks of the comparison table, in display order.
 *
 * `builder` sits before `support` rather than in its catalog position (last): support is a
 * single-row tail block and reads best as the tail.
 *
 * ⚠ This list is a hand-written mirror of the catalog's categories, so it WILL drift again -
 * `builder` was missing from it until 2026-08-02, which silently dropped all fifteen of its
 * rows (the exclusive widgets plus `sections.accountShare`) from the table with no error
 * anywhere. Hence `orderedCategories` below: an unlisted category is appended, never
 * dropped, so the worst case is a block in the wrong place instead of a block nobody sees.
 */
export const CATEGORY_ORDER: readonly string[] = ["publishing", "content", "modules", "ai", "builder", "support"];

/** Categories to render, in order: the known ones (that the catalog actually carries),
 *  then anything else the catalog has, in catalog order. */
export function orderedCategories(catalog: EntitlementCatalogEntry[]): string[] {
  const present = new Set(catalog.map((d) => d.category));
  const known = CATEGORY_ORDER.filter((c) => present.has(c));
  const rest = [...present].filter((c) => !CATEGORY_ORDER.includes(c));
  return [...known, ...rest];
}

/** Is the limit/quota "better than the previous one"? ∞ beats any finite value; a larger
 *  finite value wins. */
export function isBetterLimit(cur: number, prev: number): boolean {
  if (cur === UNLIMITED) return prev !== UNLIMITED;
  if (prev === UNLIMITED) return false;
  return cur > prev;
}

/** Features this plan ADDS over the previous one (delta). `prev = null` (the first plan)
 *  → every enabled base feature. */
export function planDelta(
  plan: { features: EntitlementMap },
  prev: { features: EntitlementMap } | null,
  catalog: EntitlementCatalogEntry[],
): EntitlementCatalogEntry[] {
  return catalog.filter((def) => {
    const cur = plan.features[def.key];
    const old = prev?.features[def.key];
    if (def.type === "boolean") return cur === true && old !== true;
    const curN = typeof cur === "number" ? cur : 0;
    if (curN === 0) return false; // not offered → do not show
    if (!prev) return true; // first plan → the base
    return isBetterLimit(curN, typeof old === "number" ? old : 0);
  });
}

/** One rendered row - either a plain catalog entry or an exclusive-widget tier roll-up. */
export interface ComparisonRow {
  /** React key. */
  key: string;
  /** The catalog entry; absent on a tier roll-up. */
  def?: EntitlementCatalogEntry;
  /** The exclusive-widget tier this row stands for; absent on a plain row. */
  tier?: 1 | 2 | 3;
  /** The tier's catalog keys (roll-up only): the label counts them, and a table cell counts
   *  how many of them a given plan actually grants. */
  tierKeys?: string[];
}

/**
 * Collapse every exclusive-widget tier in `defs` into ONE row ("Effect widgets (6)") and
 * pass everything else through. Without it the Standard card carries six and the AI card six
 * widget-name rows, which bury the plan's actual arguments; for a buyer "six effect widgets"
 * is one decision, not six. The comparison table uses the same roll-up, so the two views
 * agree on what a tier is called and how large it is.
 *
 * The roll-up row sits where the tier's FIRST key sits in `defs`, so the catalog's own order
 * survives (today that yields the ladder order 1 → 2 → 3, and it keeps following the registry
 * if that order ever changes).
 *
 * Locale-free by design: the caller turns `tier` + `tierKeys.length` into text with
 * `widgetTierLabel`, which keeps this function a pure data transform.
 */
export function rollUpWidgetTiers(defs: EntitlementCatalogEntry[]): ComparisonRow[] {
  const keysByTier = new Map<1 | 2 | 3, string[]>();
  for (const def of defs) {
    if (def.tier) keysByTier.set(def.tier, [...(keysByTier.get(def.tier) ?? []), def.key]);
  }
  const seen = new Set<number>();
  const rows: ComparisonRow[] = [];
  for (const def of defs) {
    if (!def.tier) {
      rows.push({ key: def.key, def });
      continue;
    }
    if (seen.has(def.tier)) continue;
    seen.add(def.tier);
    rows.push({ key: `widget-tier-${def.tier}`, tier: def.tier, tierKeys: keysByTier.get(def.tier) ?? [] });
  }
  return rows;
}
